import { useState, useEffect, useCallback, useMemo } from 'react';

interface UseFormDraftOptions<T> {
  storageKey: string;
  defaultValues: T;
  debounceMs?: number;
}

/**
 * Hook to persist form draft data to localStorage.
 * Automatically saves changes and restores on mount.
 *
 * @param options - Configuration options
 * @returns Object with values, setters, and clear function
 */
export function useFormDraft<T extends Record<string, unknown>>(
  options: UseFormDraftOptions<T>
) {
  const { storageKey, defaultValues, debounceMs = 500 } = options;

  // Try to load saved draft on initialization
  const initialValues = useMemo(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge saved values with defaults (in case new fields were added)
        return { ...defaultValues, ...parsed };
      }
    } catch {
      // Ignore parse errors
    }
    return defaultValues;
  }, [storageKey, defaultValues]);

  const [values, setValues] = useState<T>(initialValues);
  const [hasLoadedDraft, setHasLoadedDraft] = useState(false);

  // Check if there was a saved draft
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        setHasLoadedDraft(true);
      }
    } catch {
      // Ignore
    }
  }, [storageKey]);

  // Debounced save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        // Only save if there are non-default values
        const hasChanges = Object.keys(values).some(
          key => values[key] !== defaultValues[key]
        );
        if (hasChanges) {
          localStorage.setItem(storageKey, JSON.stringify(values));
        }
      } catch {
        // Ignore storage errors
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [values, storageKey, defaultValues, debounceMs]);

  // Update a single field
  const setValue = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear the draft
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore
    }
    setValues(defaultValues);
    setHasLoadedDraft(false);
  }, [storageKey, defaultValues]);

  // Check if form has unsaved changes from defaults
  const isDirty = useMemo(() => {
    return Object.keys(values).some(key => values[key] !== defaultValues[key]);
  }, [values, defaultValues]);

  return {
    values,
    setValues,
    setValue,
    clearDraft,
    hasLoadedDraft,
    isDirty,
  };
}
